import { THEME } from '@/constants/broker_metrics';
import { useMediaQuery, MOBILE_MEDIA_QUERY } from '@/hooks/useMediaQuery';
import type { AxisRange, Extent, PlotlyAxisInternals } from '@/types/plotlyChart';
import { BrokerMetricsObjects } from '@/types/main';
import { createChartBoxes } from '@/utils/convert';
import {
    EMPTY_MS_EXTENT,
    EMPTY_MS_TICKS,
    asGraphDiv,
    boxUnderPointer,
    categoryExtent,
    categoryTimes,
    clampAxisRange,
    cursorForRegion,
    dataDays,
    formatCategoryTick,
    formatMs,
    hideBoxTip,
    hitRegion,
    logAxisTicks,
    logRangeToMs,
    nearlyEqualExtent,
    niceLinearTicks,
    panAxisRange,
    parsePlotlyNumber,
    parseRelayoutExtent,
    placeBoxTip,
    plotlyDayStart,
    renderBoxTip,
    scaleAxisRange,
    toPlotlyMs,
    visibleCategoryTicks,
    visibleLogTicks,
    widenCategoryRange,
    yValueExtent,
} from '@/utils/plotlyChart';
import { RotateCcw } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import type { Config, PlotlyDataLayoutConfig, PlotlyHTMLElement } from 'plotly.js';
// Same webpack bundle react-plotly.js loads; @types only cover the package root.
// @ts-expect-error dist entry has no dedicated declaration
import Plotly from 'plotly.js/dist/plotly';
import createPlotlyComponent from 'react-plotly.js/factory';

const Plot = createPlotlyComponent(Plotly);

function PlotlyBox({
    data,
    showLegend = true,
    compact = false,
}: {
    readonly data: Record<string, BrokerMetricsObjects>;
    readonly showLegend?: boolean;
    readonly compact?: boolean;
}) {
    const [zoomX, setZoomX] = useState<Extent | null>(null);
    const [zoomY, setZoomY] = useState<Extent | null>(null);
    const [viewRevision, setViewRevision] = useState(0);
    const isZoomed = zoomX !== null || zoomY !== null;
    const plotlyData = useMemo(() => createChartBoxes(Object.entries(data)), [data]);
    const containerRef = useRef<HTMLDivElement>(null);
    const tipRef = useRef<HTMLDivElement>(null);
    const graphRef = useRef<PlotlyHTMLElement | null>(null);
    const xExtentRef = useRef<Extent | null>(null);
    const yExtentRef = useRef<Extent | null>(null);
    const zoomCommitRef = useRef(0);

    const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY);
    const maxDateTicks = isMobile ? 5 : 8;
    const maxYTicks = isMobile ? 6 : 8;
    let yTickFontSize = 11;
    if (compact) {
        yTickFontSize = 10;
    } else if (isMobile) {
        yTickFontSize = 9;
    }
    const days = useMemo(() => dataDays(plotlyData), [plotlyData]);
    const xCategories = useMemo(() => categoryTimes(plotlyData), [plotlyData]);
    const daysKey = days.join(',');
    useEffect(() => {
        setZoomX(null);
        setZoomY(null);
        setViewRevision((value) => value + 1);
    }, [daysKey]);
    const singleDay = days.length <= 1;
    const logAxis = useMemo(() => {
        const extent = yValueExtent(plotlyData as { lowerfence?: number[]; upperfence?: number[] }[]);
        if (!extent) {
            return {
                tickvals: EMPTY_MS_TICKS,
                range: [Math.log10(EMPTY_MS_EXTENT[0]), Math.log10(EMPTY_MS_EXTENT[1])] as Extent,
            };
        }
        return logAxisTicks(extent, maxYTicks);
    }, [plotlyData, maxYTicks]);
    const xDataExtent = useMemo(() => categoryExtent(xCategories.length), [xCategories.length]);
    const defaultVisibleXExtent = useMemo((): Extent | null => {
        if (!xDataExtent) {
            return null;
        }
        if (days.length <= 3) {
            return xDataExtent;
        }
        const thirdDay = days[2];
        let lastIndex = 0;
        xCategories.forEach((value, index) => {
            const time = toPlotlyMs(value);
            if (time !== null && plotlyDayStart(time) <= thirdDay) {
                lastIndex = index;
            }
        });
        return [-0.5, lastIndex + 0.5];
    }, [xDataExtent, days, xCategories]);
    const visibleXExtent = useMemo(
        () => (zoomX ?? defaultVisibleXExtent ?? xDataExtent ? widenCategoryRange((zoomX ?? defaultVisibleXExtent ?? xDataExtent) as Extent, xDataExtent) : null),
        [zoomX, defaultVisibleXExtent, xDataExtent]
    );
    const xTicks = useMemo(
        () => (visibleXExtent ? visibleCategoryTicks(visibleXExtent, xCategories.length, maxDateTicks) : []),
        [visibleXExtent, xCategories.length, maxDateTicks]
    );
    const yDataExtent = useMemo((): Extent | null => {
        if (compact) {
            const extent = yValueExtent(plotlyData as { lowerfence?: number[]; upperfence?: number[] }[]);
            if (!extent) {
                return EMPTY_MS_EXTENT;
            }
            return [0, extent[1] * 1.05];
        }
        return [10 ** logAxis.range[0], 10 ** logAxis.range[1]];
    }, [compact, plotlyData, logAxis.range]);
    xExtentRef.current = xDataExtent;
    yExtentRef.current = yDataExtent;
    const yTickvals = useMemo(() => {
        if (zoomY) {
            return compact ? niceLinearTicks(zoomY, maxYTicks) : visibleLogTicks(zoomY, maxYTicks);
        }
        return compact ? null : logAxis.tickvals;
    }, [zoomY, compact, maxYTicks, logAxis.tickvals]);
    const handleClearZoomState = () => {
        setZoomX(null);
        setZoomY(null);
    };

    const handleResetZoom = () => {
        handleClearZoomState();
        setViewRevision((value) => value + 1);
    };

    const handleResetZoomClick = (event: MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();
        handleResetZoom();
    };

    const handleGraphReady = (_figure: unknown, graphDiv: HTMLElement) => {
        graphRef.current = graphDiv as PlotlyHTMLElement;
    };

    useEffect(() => {
        const container = containerRef.current;
        if (!container) {
            return;
        }

        const getGraph = () => asGraphDiv(graphRef.current);

        const relayoutRange = (update: Record<string, AxisRange>) => {
            const gd = getGraph();
            if (!gd) {
                return;
            }
            void Plotly.relayout(gd, update);
        };

        type AxisDrag = {
            axis: 'x' | 'y';
            startX: number;
            startY: number;
            range: AxisRange;
            centerFraction: number;
        };
        let axisDrag: AxisDrag | null = null;
        let touchState: { cx: number; cy: number; dist: number; xRange: AxisRange } | null = null;
        let frame = 0;
        let pending: Record<string, AxisRange> | null = null;

        const flushRelayout = () => {
            frame = 0;
            if (!pending) {
                return;
            }
            const update = pending;
            pending = null;
            relayoutRange(update);
        };

        const scheduleRelayout = (update: Record<string, AxisRange>) => {
            pending = { ...pending, ...update };
            if (frame === 0) {
                frame = window.requestAnimationFrame(flushRelayout);
            }
        };

        const applyXRange = (axis: PlotlyAxisInternals, next: AxisRange) => {
            const extent = xExtentRef.current;
            if (!extent) {
                return;
            }
            const clamped = clampAxisRange(axis, next, extent, 1);
            axis.range = clamped;
            scheduleRelayout({ 'xaxis.range': clamped });
        };

        const applyYRange = (axis: PlotlyAxisInternals, next: AxisRange) => {
            const extent = yExtentRef.current;
            if (!extent) {
                return;
            }
            const clamped = clampAxisRange(axis, next, extent);
            axis.range = clamped;
            scheduleRelayout({ 'yaxis.range': clamped });
        };

        const handleWheel = (event: WheelEvent) => {
            if ((event.target as HTMLElement | null)?.closest('button')) {
                return;
            }
            const gd = getGraph();
            if (!gd) {
                return;
            }
            const region = hitRegion(gd, event.clientX, event.clientY);
            if (!region) {
                return;
            }
            event.preventDefault();
            event.stopPropagation();

            const xa = gd._fullLayout.xaxis;
            const rect = gd.getBoundingClientRect();
            const centerFraction = Math.min(1, Math.max(0, (event.clientX - rect.left - xa._offset) / xa._length));
            const absX = Math.abs(event.deltaX);
            const absY = Math.abs(event.deltaY);
            const pinch = event.ctrlKey || event.metaKey;

            if (!pinch && absX > absY) {
                applyXRange(xa, panAxisRange(xa, event.deltaX));
                return;
            }
            if (absY < 0.5 && !pinch) {
                return;
            }
            const zoom = Math.exp(Math.min(Math.max(event.deltaY, -40), 40) / 200);
            applyXRange(xa, scaleAxisRange(xa, zoom, centerFraction));
        };

        const handlePointerDown = (event: PointerEvent) => {
            if (event.pointerType === 'touch' || event.button !== 0) {
                return;
            }
            if ((event.target as HTMLElement | null)?.closest('button')) {
                return;
            }
            const gd = getGraph();
            if (!gd) {
                return;
            }
            const region = hitRegion(gd, event.clientX, event.clientY);
            if (region !== 'x' && region !== 'y') {
                return;
            }
            event.preventDefault();
            event.stopPropagation();
            const axis = region === 'x' ? gd._fullLayout.xaxis : gd._fullLayout.yaxis;
            const rect = gd.getBoundingClientRect();
            const along = region === 'x' ? event.clientX - rect.left - axis._offset : axis._offset + axis._length - (event.clientY - rect.top);
            axisDrag = {
                axis: region,
                startX: event.clientX,
                startY: event.clientY,
                range: [axis.range[0], axis.range[1]],
                centerFraction: Math.min(1, Math.max(0, along / axis._length)),
            };
            container.setPointerCapture(event.pointerId);
            container.style.cursor = region === 'x' ? 'ew-resize' : 'ns-resize';
        };

        const handlePointerMove = (event: PointerEvent) => {
            const gd = getGraph();
            if (!gd) {
                return;
            }
            if (!axisDrag) {
                const region = hitRegion(gd, event.clientX, event.clientY);
                container.style.cursor = cursorForRegion(region);
                const tip = tipRef.current;
                if (region !== 'plot' || !tip) {
                    hideBoxTip(tip);
                    return;
                }
                const hit = boxUnderPointer(gd, event.clientX, event.clientY);
                if (!hit) {
                    hideBoxTip(tip);
                    return;
                }
                renderBoxTip(tip, hit);
                placeBoxTip(container, tip, event.clientX, event.clientY);
                return;
            }
            hideBoxTip(tipRef.current);
            event.preventDefault();
            const axis = axisDrag.axis === 'x' ? gd._fullLayout.xaxis : gd._fullLayout.yaxis;
            axis.range = axisDrag.range;
            const delta = axisDrag.axis === 'x' ? event.clientX - axisDrag.startX : axisDrag.startY - event.clientY;
            const factor = Math.exp(delta / Math.max(axis._length, 1));
            const next = scaleAxisRange(axis, factor, axisDrag.centerFraction);
            if (axisDrag.axis === 'x') {
                applyXRange(axis, next);
                return;
            }
            applyYRange(axis, next);
        };

        const handlePointerUp = (event: PointerEvent) => {
            if (axisDrag && container.hasPointerCapture(event.pointerId)) {
                container.releasePointerCapture(event.pointerId);
            }
            axisDrag = null;
        };

        const touchCentroid = (touches: TouchList) => {
            const first = touches[0];
            const second = touches[1];
            const dx = second.clientX - first.clientX;
            const dy = second.clientY - first.clientY;
            return {
                cx: (first.clientX + second.clientX) / 2,
                cy: (first.clientY + second.clientY) / 2,
                dist: Math.hypot(dx, dy),
            };
        };

        const handleTouchStart = (event: TouchEvent) => {
            if (event.touches.length !== 2) {
                touchState = null;
                return;
            }
            const gd = getGraph();
            if (!gd) {
                return;
            }
            event.preventDefault();
            const touch = touchCentroid(event.touches);
            touchState = { ...touch, xRange: [gd._fullLayout.xaxis.range[0], gd._fullLayout.xaxis.range[1]] };
        };

        const handleTouchMove = (event: TouchEvent) => {
            if (event.touches.length !== 2 || !touchState) {
                return;
            }
            const gd = getGraph();
            if (!gd) {
                return;
            }
            event.preventDefault();
            const xa = gd._fullLayout.xaxis;
            const touch = touchCentroid(event.touches);
            xa.range = touchState.xRange;
            const panned = panAxisRange(xa, touch.cx - touchState.cx);
            xa.range = [panned[0], panned[1]];
            const rect = gd.getBoundingClientRect();
            const centerFraction = Math.min(1, Math.max(0, (touch.cx - rect.left - xa._offset) / xa._length));
            const pinch = touchState.dist > 8 && touch.dist > 8 ? touchState.dist / touch.dist : 1;
            applyXRange(xa, scaleAxisRange(xa, pinch, centerFraction));
        };

        const handleTouchEnd = () => {
            touchState = null;
        };

        container.addEventListener('wheel', handleWheel, { passive: false });
        container.addEventListener('pointerdown', handlePointerDown, true);
        container.addEventListener('pointermove', handlePointerMove);
        container.addEventListener('pointerup', handlePointerUp);
        const handlePointerLeave = () => {
            hideBoxTip(tipRef.current);
        };

        container.addEventListener('pointercancel', handlePointerUp);
        container.addEventListener('pointerleave', handlePointerLeave);
        container.addEventListener('touchstart', handleTouchStart, { passive: false });
        container.addEventListener('touchmove', handleTouchMove, { passive: false });
        container.addEventListener('touchend', handleTouchEnd);
        container.addEventListener('touchcancel', handleTouchEnd);

        return () => {
            if (frame !== 0) {
                window.cancelAnimationFrame(frame);
            }
            container.removeEventListener('wheel', handleWheel);
            container.removeEventListener('pointerdown', handlePointerDown, true);
            container.removeEventListener('pointermove', handlePointerMove);
            container.removeEventListener('pointerup', handlePointerUp);
            container.removeEventListener('pointercancel', handlePointerUp);
            container.removeEventListener('pointerleave', handlePointerLeave);
            container.removeEventListener('touchstart', handleTouchStart);
            container.removeEventListener('touchmove', handleTouchMove);
            container.removeEventListener('touchend', handleTouchEnd);
            container.removeEventListener('touchcancel', handleTouchEnd);
        };
    }, []);

    const layoutConfig: PlotlyDataLayoutConfig['layout'] = {
        images: [
            {
                x: 0.5,
                y: 0.3,
                sizex: 0.3,
                sizey: 0.3,
                opacity: 0.2,
                source: '/logo.svg',
                xanchor: 'right',
                xref: 'paper',
                yanchor: 'bottom',
                yref: 'paper',
            },
        ],
        yaxis: {
            title: 'Durations (ms)',
            // Combined view spans medians to multi-second spikes, so it stays
            // log, labelled with the round stops that fall inside the data's
            // own range. A single broker's range is narrow enough for a plain
            // linear axis with evenly spaced round ticks, anchored at zero.
            type: compact ? 'linear' : 'log',
            rangemode: compact ? 'tozero' : undefined,
            zeroline: false,
            color: THEME.tickTextColor,
            ...(yTickvals
                ? {
                      tickvals: yTickvals,
                      ticktext: yTickvals.map(formatMs),
                      range: zoomY ? (compact ? zoomY : ([Math.log10(Math.max(zoomY[0], 0.1)), Math.log10(Math.max(zoomY[1], 0.1))] as Extent)) : logAxis.range,
                  }
                : {}),
            tickfont: { size: yTickFontSize },
            gridcolor: '#eef0f2',
            automargin: true,
        },
        xaxis: {
            // Integer positions, one per timestamp that has data. Empty
            // frames are omitted, so the axis stays continuous.
            type: 'linear',
            side: 'bottom',
            tickmode: 'array',
            tickvals: xTicks,
            ticktext: xTicks.map((index) => formatCategoryTick(xCategories[index] ?? '')),
            ...(visibleXExtent ? { range: visibleXExtent } : {}),
            tickangle: 0,
            ticks: '',
            showgrid: false,
            tickfont: { size: isMobile ? 10 : 11, color: '#334155' },
            color: THEME.tickTextColor,
            showticklabels: xCategories.length > 0,
            automargin: false,
        },
        annotations: [],
        hovermode: false,
        // Positions are already packed per timestamp in the traces. Group
        // mode would still reserve a lane for every selected broker.
        boxmode: 'overlay',
        hoverlabel: {
            bgcolor: 'rgba(255, 255, 255, 0.98)',
            bordercolor: '#e2e8f0',
            // Monospace so the padded label column in each stat row lines up.
            font: { family: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', color: '#334155', size: 12 },
            align: 'left',
            namelength: -1,
        },
        plot_bgcolor: THEME.bg,
        paper_bgcolor: THEME.bg,
        autosize: true,
        showlegend: showLegend,
        legend: {
            x: 0.5,
            xanchor: 'center',
            y: -0.28,
            yanchor: 'top',
            font: {
                family: 'sans-serif',
                size: 12,
                color: THEME.tickTextColor,
            },
            orientation: 'h',
            bgcolor: 'transparent',
            borderwidth: 0,
        },
        margin: { t: 28, r: 28, b: (showLegend ? 96 : 56) + (singleDay ? 8 : 16), l: compact ? 44 : 56 },
        dragmode: 'pan',
        uirevision: viewRevision,
    };

    const config: Partial<Config> = {
        responsive: true,
        scrollZoom: false,
        doubleClick: false,
        showAxisDragHandles: false,
        showAxisRangeEntryBoxes: false,
        displaylogo: false,
        displayModeBar: false,
    };

    const handleRelayout = (event: Record<string, unknown>) => {
        const reset = event['xaxis.autorange'] === true || event['yaxis.autorange'] === true;
        if (reset) {
            window.clearTimeout(zoomCommitRef.current);
            handleClearZoomState();
            return;
        }

        const nextX = parseRelayoutExtent(event, 'x', parsePlotlyNumber);
        const nextY = parseRelayoutExtent(event, 'y', parsePlotlyNumber);
        if (!nextX && !nextY) {
            return;
        }
        window.clearTimeout(zoomCommitRef.current);
        zoomCommitRef.current = window.setTimeout(() => {
            if (nextX) {
                const home = defaultVisibleXExtent ?? xDataExtent;
                setZoomX(home && nearlyEqualExtent(nextX, home) ? null : nextX);
            }
            if (nextY) {
                const asMs = compact ? nextY : logRangeToMs(nextY);
                setZoomY(yDataExtent && nearlyEqualExtent(asMs, yDataExtent) ? null : asMs);
            }
        }, 160);
    };

    useEffect(() => () => window.clearTimeout(zoomCommitRef.current), []);

    return (
        <div ref={containerRef} className='relative h-full w-full overscroll-contain'>
            <div
                ref={tipRef}
                className='pointer-events-none absolute z-30 hidden rounded-md border border-slate-200 bg-white/95 px-2.5 py-2 font-mono text-xs text-slate-700 shadow-sm'
            />
            {isZoomed && (
                <button
                    type='button'
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={handleResetZoomClick}
                    aria-label='Reset chart view'
                    className='absolute right-2 top-2 z-20 inline-flex h-8 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 text-xs font-medium text-slate-600 shadow-sm hover:bg-slate-50'>
                    <RotateCcw size={14} aria-hidden='true' />
                    Reset
                </button>
            )}
            <Plot
                className='bg-primary'
                style={{ width: '100%', height: '100%' }}
                data={plotlyData as any}
                config={config}
                layout={layoutConfig}
                revision={viewRevision}
                onInitialized={handleGraphReady}
                onUpdate={handleGraphReady}
                onRelayout={handleRelayout}
                useResizeHandler={true}
            />
        </div>
    );
}

export default PlotlyBox;
